import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { apiClient } from '../../lib/api-client';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs';
import 'prismjs/components/prism-yaml';
import 'prismjs/themes/prism-tomorrow.css';
import yaml from 'js-yaml';
import { Skeleton } from '../ui/Skeleton';
import { MasterApprovalsDefaultsForm } from './master-approvals-defaults-form/MasterApprovalsDefaultsForm';
import {
  approvalsDocToYaml,
  cloneDoc,
  parseApprovalsYaml,
  type ApprovalsConfigDoc,
} from '../../lib/approvals-config-form-utils';

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  isMaster?: boolean;
  tenantCode?: string | null;
};

type EditorMode = 'form' | 'yaml';

export function ApprovalsConfigEditDialog({ open, onClose, onSuccess, isMaster = false, tenantCode }: Props) {
  const [mode, setMode] = useState<EditorMode>('form');
  const [yamlContent, setYamlContent] = useState('');
  const [formDoc, setFormDoc] = useState<ApprovalsConfigDoc>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const effectiveTenant = useMemo(() => String(tenantCode || '').toLowerCase(), [tenantCode]);

  useEffect(() => {
    if (!open) return;
    setMode(isMaster ? 'form' : 'yaml');
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isMaster, effectiveTenant]);

  const title = isMaster ? 'Edit Master Approvals Defaults' : `Edit Tenant Approvals: ${effectiveTenant.toUpperCase()}`;
  const description = isMaster
    ? 'Edit approvals defaults — form for common fields, YAML for advanced edits. Integration and consent are configured per-tenant.'
    : 'Edit tenant-specific approvals configuration (approvals: subtree). Leave empty to inherit from master defaults.';

  const load = async () => {
    setLoading(true);
    setError(null);
    setParseError(null);
    try {
      const res = isMaster ? await apiClient.getApprovalsMasterConfig() : await apiClient.getApprovalsTenantConfig(effectiveTenant);
      const current = res?.approvals || {};
      const dumped = yaml.dump(current, { noRefs: true, lineWidth: 160 });
      setYamlContent(dumped || '');
      setFormDoc(current as ApprovalsConfigDoc);
    } catch (e: any) {
      setError(e?.message || 'Failed to load approvals config');
    } finally {
      setLoading(false);
    }
  };

  const validateYaml = (content: string): boolean => {
    try {
      yaml.load(content);
      setParseError(null);
      return true;
    } catch (err: any) {
      setParseError(`Invalid YAML: ${err.message}`);
      return false;
    }
  };

  const switchMode = (next: EditorMode) => {
    if (next === mode) return;

    if (next === 'yaml') {
      try {
        setYamlContent(approvalsDocToYaml(formDoc));
        setParseError(null);
      } catch (e: any) {
        setParseError(e.message);
        return;
      }
    }

    if (mode === 'yaml' && next === 'form') {
      try {
        setFormDoc(parseApprovalsYaml(yamlContent));
        setParseError(null);
      } catch (e: any) {
        setParseError(e.message);
        return;
      }
    }

    setMode(next);
  };

  const handleSave = async () => {
    let contentToSave = yamlContent;

    if (isMaster && mode === 'form') {
      try {
        contentToSave = approvalsDocToYaml(formDoc);
        setParseError(null);
      } catch (e: any) {
        setParseError(e.message);
        return;
      }
    }

    if (!validateYaml(contentToSave)) return;

    setSaving(true);
    setError(null);
    try {
      const parsed = (yaml.load(contentToSave) as any) || {};
      if (isMaster) {
        await apiClient.updateApprovalsMasterConfig(parsed);
      } else {
        await apiClient.updateApprovalsTenantConfig(effectiveTenant, parsed);
      }
      onSuccess();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to save approvals config');
    } finally {
      setSaving(false);
    }
  };

  const handleGrantConsent = async () => {
    if (isMaster) return;
    try {
      const res = await apiClient.getApprovalsTenantConsentUrl(effectiveTenant);
      if (res?.url) {
        window.open(res.url, '_blank');
      } else {
        alert(res?.error || 'Could not generate consent URL. Configure microsoft.client_id and microsoft.tenant_id for this tenant and save.');
      }
    } catch (e: any) {
      alert(`Failed to get consent URL: ${e?.message || String(e)}`);
    }
  };

  const handleYamlChange = (newValue: string) => {
    setYamlContent(newValue);
    try {
      yaml.load(newValue);
      setParseError(null);
    } catch (err: any) {
      setParseError(`Invalid YAML: ${err.message}`);
    }
  };

  const exampleConfig = isMaster
    ? `# Master approvals defaults only (no integration here)
defaults:
  expiration_hours: 24
  notify_all_by_default: true
`
    : `# Tenant approvals configuration
enabled: true
microsoft:
  enabled: true
  client_id: ":${effectiveTenant}-ms-approvals-client-id"
  client_secret: ":${effectiveTenant}-ms-approvals-client-secret"
  tenant_id: ":${effectiveTenant}-ms-approvals-tenant-id"
defaults:
  expiration_hours: 24
  notify_all_by_default: true
`;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent maxWidth="4xl" className="max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription>{description}</DialogDescription>
            </div>
            {isMaster ? (
              <div className="flex items-center border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shrink-0">
                <button
                  type="button"
                  onClick={() => switchMode('form')}
                  className={`px-3 py-1.5 text-xs font-medium ${
                    mode === 'form'
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  Form
                </button>
                <button
                  type="button"
                  onClick={() => switchMode('yaml')}
                  className={`px-3 py-1.5 text-xs font-medium ${
                    mode === 'yaml'
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  YAML
                </button>
              </div>
            ) : null}
          </div>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3 min-h-0 overflow-y-auto">
          {loading && (
            <div className="space-y-2 py-4">
              <Skeleton className="h-64 w-full" />
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-800 dark:text-red-200">
              {error}
            </div>
          )}

          {parseError && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-amber-800 dark:text-amber-200">
              {parseError}
            </div>
          )}

          {!loading && isMaster && mode === 'form' && (
            <MasterApprovalsDefaultsForm doc={formDoc} onChange={(next) => setFormDoc(cloneDoc(next))} />
          )}

          {!loading && (!isMaster || mode === 'yaml') && (
            <>
              <div className="relative">
                <div className="bg-gray-900 rounded-lg overflow-hidden border-2 border-gray-700 focus-within:border-blue-500">
                  <div className="max-h-[min(400px,50vh)] overflow-auto custom-scrollbar">
                    <Editor
                      value={yamlContent}
                      onValueChange={handleYamlChange}
                      highlight={(code: string) => highlight(code, languages.yaml, 'yaml')}
                      padding={16}
                      style={{
                        fontFamily: '"Fira Code", "Fira Mono", monospace',
                        fontSize: 13,
                        backgroundColor: '#1e293b',
                        color: '#e2e8f0',
                        minHeight: 240,
                      }}
                      className="outline-none"
                    />
                  </div>
                </div>
              </div>

              <details>
                <summary className="cursor-pointer text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200">
                  Show example configuration
                </summary>
                <div className="mt-2 bg-gray-100 dark:bg-gray-800 rounded-lg p-3">
                  <pre className="text-xs text-gray-700 dark:text-gray-300 overflow-x-auto whitespace-pre-wrap">
                    {exampleConfig}
                  </pre>
                </div>
              </details>
            </>
          )}
        </DialogBody>

        <DialogFooter>
          <Button onClick={onClose} disabled={saving} variant="secondary">
            Cancel
          </Button>
          {!isMaster && (
            <Button type="button" variant="outline" onClick={handleGrantConsent} disabled={saving || loading}>
              Grant MS Admin Consent
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving || loading || !!parseError}>
            {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
